<script setup>
import { computed, inject } from 'vue';
import { openCount } from '../openTrails.js';
import { accentParts, hasLink, styleVars } from '../Trailhead/shared.js';
import { heroFocusPosition, heroHasContent } from '../heroImage.js';

// The hero as the Hub draws it: the hub page's headline area — date or
// eyebrow, a 38–44px headline, the "Updated" sub-line — on the org's
// colour band, with the hub's count tiles on the right. 'split' puts
// the photo beside the words in a rounded frame; 'full' keeps the band
// and shows no photo behind the words, because the band IS the hub's
// look. Reads the same config as ThHero and ByHero.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const split = computed(() => props.config.layout === 'split' && props.config.image);
const titleParts = computed(() => accentParts(props.config.title));
const hasInlineAccent = computed(() => titleParts.value.some((p) => p.accent));
const focusStyle = computed(() => ({ objectPosition: heroFocusPosition(props.config) }));

// The hub's tiles: trails open of total, networks open of total. Same
// rows and open rule (openTrails.js) ThHero's status pill reads.
const tiles = computed(() => {
    if (!props.config.show_status || props.site_data?.show_status === false) return [];
    const out = [];
    const trails = props.site_data?.trails || [];
    const networks = props.site_data?.networks || [];
    if (trails.length) out.push({ label: 'Trails open', open: openCount(trails), total: trails.length });
    if (networks.length > 1) out.push({ label: 'Networks open', open: openCount(networks), total: networks.length });
    return out;
});

// Nothing for a visitor when there is nothing to say (heroHasContent);
// the editor canvas keeps the empty hero so there is something to click.
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => heroHasContent(props.config) || tiles.value.length > 0);
</script>

<template>
    <section v-if="hasContent || editing" :id="config.anchor_id || null" class="th-block hub-band hub-hero" :style="styleVars(config)">
        <div class="th-wrap hub-hero__inner" :class="{ 'hub-hero__inner--split': split }">
            <div class="hub-hero__text">
                <p v-if="config.eyebrow" class="hub-eyebrow-on-band">{{ config.eyebrow }}</p>
                <h1 v-if="titleParts.length || config.title_accent || editing" class="hub-hero__title">
                    <template v-for="(part, i) in titleParts" :key="i">
                        <span v-if="part.accent" class="hub-hero__accent">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                    <template v-if="!hasInlineAccent && config.title_accent">
                        {{ ' ' }}<span class="hub-hero__accent">{{ config.title_accent }}</span>
                    </template>
                </h1>
                <p v-if="config.subtitle" class="hub-soft hub-hero__lede">{{ config.subtitle }}</p>
                <div v-if="hasLink(config.cta_primary) || hasLink(config.cta_secondary)" class="hub-hero__ctas">
                    <a v-if="hasLink(config.cta_primary)" :href="config.cta_primary.href || '#'" class="th-btn th-btn--light">{{ config.cta_primary.label }}</a>
                    <a v-if="hasLink(config.cta_secondary)" :href="config.cta_secondary.href || '#'" class="th-btn th-btn--ghost-light">{{ config.cta_secondary.label }}</a>
                </div>
            </div>
            <div v-if="tiles.length && !split" class="hub-hero__tiles">
                <div v-for="t in tiles" :key="t.label" class="hub-tile">
                    <div class="hub-tile__label">{{ t.label }}</div>
                    <div class="hub-tile__value">{{ t.open }}<span> / {{ t.total }}</span></div>
                </div>
            </div>
            <div v-if="split" class="th-media th-media--on-accent hub-hero__photo">
                <img :src="config.image" alt="" :style="focusStyle">
            </div>
        </div>
        <div v-if="split && tiles.length" class="th-wrap hub-hero__tiles hub-hero__tiles--under">
            <div v-for="t in tiles" :key="t.label" class="hub-tile">
                <div class="hub-tile__label">{{ t.label }}</div>
                <div class="hub-tile__value">{{ t.open }}<span> / {{ t.total }}</span></div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.hub-hero__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 22px 32px;
    padding-top: 28px;
    padding-bottom: 34px;
}
.hub-hero__inner--split { align-items: center; }
.hub-hero__text { flex: 1 1 360px; min-width: 0; }
.hub-hero__title {
    margin: 8px 0 0;
    font-size: clamp(32px, 5vw, 44px);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1.02;
    text-wrap: pretty;
    color: var(--text-heading, inherit);
}
.hub-hero__accent { color: var(--hub-detail, #dd9c34); }
.hub-hero__lede { font-size: 15px; line-height: 1.5; margin: 12px 0 0; max-width: 520px; text-wrap: pretty; }
.hub-hero__ctas { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 18px; }
.hub-hero__tiles { display: flex; gap: 10px; }
.hub-hero__tiles--under { padding-bottom: 30px; margin-top: -10px; }
.hub-hero__photo { flex: 1 1 320px; aspect-ratio: 16 / 10; border: 1px solid var(--hub-head-line, rgba(255, 255, 255, .18)); }
</style>
